<template>
  <div class="space-y-3">
    <label class="text-sm font-medium text-gray-700">{{ label }}</label>
    
    <!-- Autocomplete de recherche -->
    <div class="relative">
      <input 
        v-model.trim="searchTerm"
        type="text"
        :placeholder="placeholder"
        @input="handleSearch"
        @focus="showSuggestions = true"
        @blur="handleBlur"
        class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
      />
      
      <!-- Liste des suggestions -->
      <div 
        v-if="showSuggestions && suggestions.length > 0"
        class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
      >
        <div 
          v-for="product in suggestions" 
          :key="product.id_produit"
          @click="selectProduct(product)"
          class="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="text-xs font-mono text-gray-400">#{{ product.id_produit }}</span>
              <span class="text-sm font-medium text-gray-900">{{ product.reference }}</span>
            </div>
            <span class="text-xs text-gray-500">{{ product.prix }}€</span>
          </div>
          <div v-if="product.reference_base" class="text-xs text-gray-500 ml-14">
            Réf. base: {{ product.reference_base }}
          </div>
        </div>
      </div>
      
      <!-- Aucun résultat -->
      <div 
        v-if="showSuggestions && searchTerm.length >= 2 && suggestions.length === 0 && !isSearching"
        class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3"
      >
        <p class="text-sm text-gray-500">Aucun produit trouvé</p>
      </div>
      
      <!-- Chargement -->
      <div 
        v-if="isSearching"
        class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3"
      >
        <p class="text-sm text-gray-500">
          <i class="fas fa-spinner fa-spin mr-2"></i>
          Recherche en cours...
        </p>
      </div>
    </div>
    
    <!-- Liste des produits sélectionnés -->
    <div v-if="modelValue.length > 0" class="space-y-2">
      <div v-for="product in modelValue" :key="product.id_produit" class="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <span class="text-xs font-mono text-gray-400">#{{ product.id_produit }}</span>
            <span class="text-sm font-medium text-gray-700">{{ product.reference }}</span>
            <span v-if="product.prix" class="text-xs text-gray-500">({{ product.prix }}€)</span>
          </div>
        </div>
        <button 
          type="button"
          @click="removeProduct(product.id_produit)"
          class="text-red-600 hover:text-red-800"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
    <div v-else class="text-sm text-gray-500">{{ emptyMessage }}</div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import type { Produit } from '@/types/produit'
import { searchProductsByReference } from '@/api/produits'
import { useAuthStore } from '@/stores/auth'

interface Props {
  modelValue: Produit[]
  label?: string
  placeholder?: string
  emptyMessage?: string
}

interface Emits {
  (e: 'update:modelValue', value: Produit[]): void
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Référence(s) à associer :',
  placeholder: 'Rechercher un produit par référence ou ID...',
  emptyMessage: 'Aucun produit associé'
})

const emit = defineEmits<Emits>()

const authStore = useAuthStore()
const searchTerm = ref('')
const suggestions = ref<Produit[]>([])
const showSuggestions = ref(false)
const isSearching = ref(false)
let searchTimeout: ReturnType<typeof setTimeout> | null = null

// Recherche de produits avec debounce
const handleSearch = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  
  const term = searchTerm.value.trim()
  
  if (term.length < 2) {
    suggestions.value = []
    return
  }
  
  isSearching.value = true
  
  searchTimeout = setTimeout(async () => {
    try {
      const vendeurId = authStore.user?.id_vendeur
      if (!vendeurId) {
        return
      }
      
      const results = await searchProductsByReference(vendeurId, term, 20)
      // Filtrer les produits déjà sélectionnés
      suggestions.value = results.filter(
        p => !props.modelValue.some(sp => sp.id_produit === p.id_produit)
      )
    } catch (error) {
      // Erreur silencieuse - les produits restent vides
    } finally {
      isSearching.value = false
    }
  }, 300)
}

// Sélectionner un produit
const selectProduct = (product: Produit) => {
  if (!props.modelValue.some(p => p.id_produit === product.id_produit)) {
    emit('update:modelValue', [...props.modelValue, product])
  }
  searchTerm.value = ''
  suggestions.value = []
  showSuggestions.value = false
}

// Retirer un produit
const removeProduct = (idProduit: number) => {
  emit('update:modelValue', props.modelValue.filter(p => p.id_produit !== idProduit))
}

// Gérer le blur de l'input
const handleBlur = () => {
  setTimeout(() => {
    showSuggestions.value = false
  }, 200)
}

// Réinitialiser les suggestions quand modelValue change
watch(() => props.modelValue, () => {
  if (searchTerm.value) {
    const term = searchTerm.value.trim()
    if (term.length >= 2) {
      suggestions.value = suggestions.value.filter(
        p => !props.modelValue.some(sp => sp.id_produit === p.id_produit)
      )
    }
  }
})
</script>

